import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { useSignalR } from '../hooks/useSignalR';
import { API_CONFIG } from '../config/api';

// Sound utilities
const playAdminSound = () => {
  try {
    const audio = new Audio('/sounds/admin-notification.mp3');
    audio.volume = 0.5;
    audio.play().catch(() => {}); // Ignore autoplay errors
  } catch {}
};

const playCustomerSound = () => {
  try {
    const audio = new Audio('/sounds/customer-notification.mp3');
    audio.volume = 0.5;
    audio.play().catch(() => {}); // Ignore autoplay errors
  } catch {}
};

interface Notification {
  id: string;
  title: string;
  body: string;
  type: string;
  referenceId?: string;
  targetUrl?: string;
  isRead: boolean;
  createdAt: string;
}

interface NotificationContextType {
  adminNotifications: Notification[];
  customerNotifications: Notification[];
  adminUnreadCount: number;
  customerUnreadCount: number;
  fetchAdminNotifications: () => Promise<void>;
  fetchCustomerNotifications: () => Promise<void>;
  markAsRead: (id: string, isAdmin: boolean) => Promise<void>;
  markAllAsRead: (isAdmin: boolean) => Promise<void>;
  disconnectSignalR: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [adminNotifications, setAdminNotifications] = useState<Notification[]>([]);
  const [customerNotifications, setCustomerNotifications] = useState<Notification[]>([]);
  const [adminUnreadCount, setAdminUnreadCount] = useState(0);
  const [customerUnreadCount, setCustomerUnreadCount] = useState(0);

  const { isAuthenticated, user, accessToken } = useSelector((state: RootState) => state.auth);
  const isAdmin = user?.roles?.includes('Admin');

  // Track processed notification IDs to prevent duplicates (persists across renders)
  const processedAdminIds = useRef<Set<string>>(new Set());
  const processedCustomerIds = useRef<Set<string>>(new Set());

  // Handle real-time admin notification
  const handleAdminNotification = useCallback((notification: any) => {
    const notificationId = notification.id?.toString();
    if (!notificationId) return;
    
    // Prevent duplicate processing
    if (processedAdminIds.current.has(notificationId)) {
      return;
    }
    processedAdminIds.current.add(notificationId);

    const newNotification = { ...notification, isRead: false };
    
    let isDuplicate = false;
    setAdminNotifications(prev => {
      // Also check if already in list
      if (prev.some(n => n.id === notificationId)) {
        isDuplicate = true;
        return prev;
      }
      return [newNotification, ...prev];
    });
    
    // Only play sound and show toast if not a duplicate
    if (!isDuplicate) {
      setAdminUnreadCount(prev => prev + 1);
      playAdminSound();
      toast.info(notification.title, {
        description: notification.body,
        action: notification.targetUrl ? {
          label: 'View',
          onClick: () => window.location.href = notification.targetUrl,
        } : undefined,
        duration: 8000,
      });
    }
  }, []);

  // Handle real-time customer notification
  const handleCustomerNotification = useCallback((notification: any) => {
    const notificationId = notification.id?.toString();
    if (!notificationId) return;
    
    // Prevent duplicate processing
    if (processedCustomerIds.current.has(notificationId)) {
      return;
    }
    processedCustomerIds.current.add(notificationId);

    const newNotification = { ...notification, isRead: false };
    
    let isDuplicate = false;
    setCustomerNotifications(prev => {
      // Also check if already in list
      if (prev.some(n => n.id === notificationId)) {
        isDuplicate = true;
        return prev;
      }
      return [newNotification, ...prev];
    });
    
    // Only play sound and show toast if not a duplicate
    if (!isDuplicate) {
      setCustomerUnreadCount(prev => prev + 1);
      playCustomerSound();
      toast.info(notification.title, {
        description: notification.body,
        action: notification.targetUrl ? {
          label: 'View',
          onClick: () => window.location.href = notification.targetUrl,
        } : undefined,
        duration: 8000,
      });
    }
  }, []);

  // Connect to SignalR
  const { disconnect: disconnectSignalR } = useSignalR({
    onAdminNotification: isAdmin ? handleAdminNotification : undefined,
    onCustomerNotification: handleCustomerNotification,
  });

  // Clear notifications and disconnect when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setAdminNotifications([]);
      setCustomerNotifications([]);
      setAdminUnreadCount(0);
      setCustomerUnreadCount(0);
      processedAdminIds.current.clear();
      processedCustomerIds.current.clear();
      disconnectSignalR();
    }
  }, [isAuthenticated, disconnectSignalR]);

  const fetchAdminNotifications = useCallback(async () => {
    if (!isAdmin || !accessToken) return;
    
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/admin/notifications?pageSize=5`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();
      if (data.isSuccess) {
        setAdminNotifications(data.result.notifications);
      }

      // Fetch unread count
      const countResponse = await fetch(`${API_CONFIG.API_URL}/admin/notifications/unread-count`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const countData = await countResponse.json();
      if (countData.isSuccess) {
        setAdminUnreadCount(countData.result.unreadCount);
      }
    } catch (error) {
      console.error('Failed to fetch admin notifications:', error);
    }
  }, [isAdmin, accessToken]);

  const fetchCustomerNotifications = useCallback(async () => {
    if (!isAuthenticated || !accessToken) return;
    
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/notifications?pageSize=5`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();
      if (data.isSuccess) {
        setCustomerNotifications(data.result.notifications);
      }

      // Fetch unread count
      const countResponse = await fetch(`${API_CONFIG.API_URL}/notifications/unread-count`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const countData = await countResponse.json();
      if (countData.isSuccess) {
        setCustomerUnreadCount(countData.result.unreadCount);
      }
    } catch (error) {
      console.error('Failed to fetch customer notifications:', error);
    }
  }, [isAuthenticated, accessToken]);

  const markAsRead = useCallback(async (id: string, isAdminNotification: boolean) => {
    if (!accessToken) return;
    
    const endpoint = isAdminNotification 
      ? `${API_CONFIG.API_URL}/admin/notifications/${id}/read`
      : `${API_CONFIG.API_URL}/notifications/${id}/read`;

    try {
      await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (isAdminNotification) {
        setAdminNotifications(prev => 
          prev.map(n => n.id === id ? { ...n, isRead: true } : n)
        );
        setAdminUnreadCount(prev => Math.max(0, prev - 1));
      } else {
        setCustomerNotifications(prev => 
          prev.map(n => n.id === id ? { ...n, isRead: true } : n)
        );
        setCustomerUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, [accessToken]);

  const markAllAsRead = useCallback(async (isAdminNotification: boolean) => {
    if (!accessToken) return;
    
    const endpoint = isAdminNotification 
      ? `${API_CONFIG.API_URL}/admin/notifications/read-all`
      : `${API_CONFIG.API_URL}/notifications/read-all`;

    try {
      await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (isAdminNotification) {
        setAdminNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        setAdminUnreadCount(0);
      } else {
        setCustomerNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        setCustomerUnreadCount(0);
      }
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, [accessToken]);

  // Notifications are now loaded on-demand when bell icon is clicked
  // No auto-loading on mount

  return (
    <NotificationContext.Provider
      value={{
        adminNotifications,
        customerNotifications,
        adminUnreadCount,
        customerUnreadCount,
        fetchAdminNotifications,
        fetchCustomerNotifications,
        markAsRead,
        markAllAsRead,
        disconnectSignalR,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
